import classNames from 'classnames';
import moment from 'moment';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { makeStyles } from '@mui/styles';

import { Brand, Organization } from '@vestahealthcare/common/models';
import {
  EMPTY,
  NO_TIME_DATE_FORMAT,
} from '@vestahealthcare/common/utils/constants';

import {
  BackgroundColors,
  CollapseIcon,
  Colors,
  EmptyState,
  Fonts,
  IconButton,
  Table,
  UpdatedBy,
} from 'styleguide-v2';

import { CacheServices } from 'dash/src/services';
import { sortBy } from 'lodash';

export type OrganizationExtended = Organization & {
  collapsed: boolean;
  detail: ReactNode;
};
interface Props {
  child?: number;
  referrals: Organization[];
  originals: Organization[];
  onEdit: (
    organization: Organization,
    child: boolean,
    grandchild: boolean,
  ) => void;
  onOpenRow: (organization: OrganizationExtended) => void;
}

const COLUMNS = (
  onEdit: (
    organization: Organization,
    child: boolean,
    grandchild: boolean,
  ) => void,
  toggleDetail: (organization: Organization) => void,
  brandList: Brand[],
  childLevel: number,
) => [
  {
    headerName: 'Name',
    component: ({ name }: Organization) => (
      <span style={{ paddingLeft: `${childLevel * 1.25}rem` }}>
        {name || EMPTY}
      </span>
    ),
    sortable: true,
  },
  { headerName: 'Abbr', field: 'abbr', width: 150 },
  {
    headerName: 'Type',
    component: ({ type }: Organization) => type?.name || EMPTY,
    width: 120,
  },
  {
    headerName: 'Brand',
    component: ({ brandId }: Organization) =>
      brandList?.find(({ id }) => id === brandId)?.name || EMPTY,
    width: 120,
  },
  { headerName: 'Status', field: 'status.name', width: 150 },
  {
    headerName: 'Start Date',
    component: (row: Organization) =>
      row.startDate ? moment(row.startDate).format(NO_TIME_DATE_FORMAT) : EMPTY,
    width: 120,
  },
  {
    headerName: 'End Date',
    component: (row: Organization) =>
      row.endDate ? moment(row.endDate).format(NO_TIME_DATE_FORMAT) : EMPTY,
    width: 120,
  },
  { headerName: 'Medicare', field: 'medicareDiscovery', width: 100 },
  {
    headerName: 'Update At',
    width: 200,
    component: ({ updatedAt, updatedBy }: OrganizationExtended) =>
      updatedAt && <UpdatedBy date={updatedAt} user={updatedBy?.fullName} />,
  },
  {
    headerName: '',
    width: 75,
    component: (row: OrganizationExtended) => (
      <div className="flex min-gap">
        <IconButton
          onClick={(evt) => {
            evt?.stopPropagation();
            onEdit(row, true, false);
          }}
          size="small"
        >
          <EditIcon />
        </IconButton>
        {row.hasChildren ? (
          <IconButton onClick={() => toggleDetail(row)} size="small">
            <CollapseIcon open={row.collapsed} />
          </IconButton>
        ) : (
          <div style={{ width: '4rem' }} />
        )}
      </div>
    ),
  },
];

const useStyles = makeStyles({
  table: {
    '&& tbody': {
      '& td': {
        fontSize: `calc(${Fonts.fontSize} * 0.75)`,
      },
      '& a': {
        fontSize: `calc(${Fonts.fontSize} * 0.75)`,
      },
      '& > tr.detail > td': {
        background: Colors.white,
        padding: '0',
      },
    },
  },
  noPaddingTable: {
    '&& thead': {
      display: 'none',
    },
    '&& tbody': {
      borderRadius: 0,
      boxShadow: 'none',

      '& tr td': {
        backgroundColor: `${BackgroundColors.lighterGray}!important`,
      },
    },
    '&& tbody tbody': {
      borderLeft: 0,
      borderRight: 0,
    },
  },
});

const ReferralTable = (props: Props) => {
  const styles = useStyles();
  const { referrals, originals, onEdit } = props;
  const [sortedReferrals, setSortedReferrals] = useState(referrals);
  const [brandList, setBrandList] = useState<Brand[]>([]);

  const getInitialData = async () => {
    const brands = await CacheServices.getOrganizationBrands();
    setBrandList(brands);
  };

  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    setSortedReferrals([...referrals]);
  }, [referrals]);

  const sortDataByName = (direction?: string) => {
    setSortedReferrals(
      [...referrals].sort(
        (a: Organization, b: Organization) =>
          a.name.localeCompare(b.name) * (direction === 'desc' ? -1 : 1),
      ),
    );
  };

  const toggleDetail = (referral: Organization) => {
    const index = sortedReferrals.findIndex(({ id }) => id === referral.id);
    const item = sortedReferrals[index] as OrganizationExtended;
    const children: Organization[] = [];

    if (item.children) {
      item.children.forEach((ref) => {
        const objToPush = originals?.find((y) => y.id === ref.id);
        if (objToPush) children.push(objToPush);
      });
    }

    item.collapsed = !item.collapsed;
    if (item.collapsed) {
      const isChild = !!props.child;
      item.detail = createChildTable(
        originals,
        children,
        (o, child, grandChild) =>
          onEdit(o, child || !isChild, grandChild || !!isChild),
        props.onOpenRow,
        (props.child || 0) + 1,
      );
    } else {
      delete item.detail;
    }
    props.onOpenRow(item);
    setSortedReferrals([...sortedReferrals]);
  };

  const columns = useMemo(
    () => COLUMNS(onEdit, toggleDetail, brandList, props.child || 0),
    [brandList, onEdit, toggleDetail, props.child],
  );

  return referrals?.length ? (
    <Table
      className={classNames(styles.table, props.child && styles.noPaddingTable)}
      config={{
        columns,
        compact: true,
        data: sortedReferrals,
        detail: true,
        paginationOptions: props.child ? [-1] : [50, 100],
      }}
      data-cy="referral-table"
      onSort={(column) => sortDataByName(column.direction)}
    />
  ) : (
    <EmptyState />
  );
};

export const createChildTable = (
  allReferrals: Organization[],
  children: Organization[],
  onEdit: (
    organization: Organization,
    child: boolean,
    grandchild: boolean,
  ) => void,
  onOpenRow: (organization: OrganizationExtended) => void,
  childLevel?: number,
) => (
  <ReferralTable
    originals={allReferrals}
    referrals={sortBy(children, 'name')}
    onEdit={(o, child, grandChild) =>
      onEdit(o, child || !childLevel, grandChild || !!childLevel)
    }
    onOpenRow={onOpenRow}
    child={childLevel}
  />
);

export default ReferralTable;
